import React from 'react';
import { Form, FormSpy } from 'react-final-form';
import { Box, Container } from '@mui/material';
import Typography from '../../modules/components/Typography';
import { password, required } from '../../modules/form/validation';
import FormButton from '../../modules/form/FormButton';
import FormFeedback from '../../modules/form/FormFeedback';
import { FORM_ERROR } from 'final-form';
import Snackbar from '../../modules/components/Snackbar';
import Paper from '../../modules/components/Paper';
import { useLocation, useNavigate } from 'react-router-dom';
import PasswordRFTextField from '../../modules/components/PasswordRFTextField';
import { changeUserPassword, getUserCurrentlyAuthenticated } from '../../modules/components/authentication';
import logger from '../../modules/components/logger';

export default function ChangePassword() {
  const navigate = useNavigate();
  const {state} = useLocation();

  const [open, setOpen] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);
  const [user, setUser] = React.useState({});
  const [disabled, setDisabled] = React.useState(false)

  React.useEffect(() => {
    setOpen(state?.open || false);
    setUser(state?.user || {});
    setDisabled(state?.disabled || false)
  }, [state]);

  const handleClose = () => {
    setOpen(false);
  }

  const validate = (values) => {
    const errors = required(['newPassword'], values);

    if (!errors.newPassword) {
      const newPasswordError = password(values.newPassword);
      if (newPasswordError) {
        errors.newPassword = newPasswordError;
      }
    }

    setIsValid(Object.keys(errors).length === 0);

    return errors;
  };

  const handleSubmit = ({ currentPassword, newPassword }) => {
    const DEFAULT_ERROR_MESSAGE = "Error, please try again later.";
    return getUserCurrentlyAuthenticated()
      .then((res) => {
        if (res.username) {
          setUser(res);
          return changeUserPassword(res, currentPassword, newPassword)
            .then((res) => {
              if (res === "SUCCESS")
                navigate('/dashboard/change-password', { state: { open: true, user, disabled: true } });
              return;
            })
            .catch((reason) => {
              logger.error(reason);
              if (reason.code === 'NotAuthorizedException')
                return {[FORM_ERROR]: "Error: Incorrect current password. Try again."};
              else
                return {[FORM_ERROR]: DEFAULT_ERROR_MESSAGE};
            })
        }
        else {
          return {[FORM_ERROR]: DEFAULT_ERROR_MESSAGE};
        }
      })
      .catch((reason) => {
        logger.error(reason);
        return {[FORM_ERROR]: DEFAULT_ERROR_MESSAGE}
      })
  };

  return (
    <React.Fragment>
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper
            background="light"
            sx={{ py: { xs: 4, md: 8 }, px: { xs: 3, md: 6 } }}
          >
            <React.Fragment>
              <Typography variant="h3" gutterBottom marked="center" align="center">
                Change Password
              </Typography>
            </React.Fragment>
            <Form
              onSubmit={handleSubmit}
              subscription={{ submitting: true, dirty: true }}
              validate={validate}
            >
              {({ handleSubmit: handleSubmit2, submitting, dirty }) => (
                <Box 
                  component="form" 
                  onSubmit={handleSubmit2}
                  noValidate 
                  sx={{ mt: 6 }}
                >
                  <PasswordRFTextField
                    name="currentPassword"
                    label="Current Password"
                    disabled={submitting || disabled}
                    autoComplete="current-password"
                    dirty={dirty}
                  />
                  <PasswordRFTextField
                    name="newPassword"
                    label="New Password"
                    disabled={submitting || disabled}
                    autoComplete="new-password"
                    dirty={dirty}
                  />
                  <FormSpy subscription={{ submitError: true }}>
                    {({ submitError }) =>
                      submitError ? (
                        <FormFeedback error sx={{ mt: 2 }}>
                          {submitError}
                        </FormFeedback>
                      ) : null
                    }
                  </FormSpy>
                  <FormButton
                    sx={{ mt: 3, mb: 2 }}
                    disabled={!isValid || submitting || disabled}
                    size="large"
                    color="secondary"
                    fullWidth
                  >
                    {submitting ? 'In progress…' : 'Submit'}
                  </FormButton>
                </Box>
              )}
            </Form>
          </Paper>
        </Box>
      </Container>
      <Snackbar
        open={open}
        closeFunc={handleClose}
        message="Success: Your password was changed."
      />
    </React.Fragment>
  );
}